import { forwardRef, useCallback } from 'react';
import classNames from 'classnames';

import { isSubmitKey } from 'Lib/utils/keyboardUtils';

import { commonButtonStyle } from 'Styles/sharedStyle';

/**
 * Handle a11y for clickable div
 */
type GenericElement = keyof React.JSX.IntrinsicElements | React.JSXElementConstructor<any>;
type Props<TElement extends GenericElement> = {
  renderTag?: TElement;
} & React.ComponentProps<TElement>;

const ClickableDivInner = <TElement extends GenericElement = 'div'>(
  props: Props<TElement>,
  ref: React.ForwardedRef<TElement>
) => {
  const { className, onClick, children, renderTag = 'div' as TElement, ...restProps } = props;

  const handleOnKeyDown = useCallback<React.KeyboardEventHandler>(
    (e) => {
      if (isSubmitKey(e.code, false)) {
        if (onClick) {
          onClick(e as unknown as React.MouseEvent<any>);
        }
      }
    },
    [onClick]
  );

  const hasClickHandler = !!onClick;

  const RenderTag = renderTag as React.ElementType;
  return (
    <RenderTag
      className={classNames(className, {
        'Cur(p)': hasClickHandler,
        [commonButtonStyle]: hasClickHandler,
      })}
      onClick={onClick}
      onKeyDown={hasClickHandler ? handleOnKeyDown : undefined}
      tabIndex={hasClickHandler ? '0' : undefined}
      data-testid="clickable-div"
      ref={ref}
      role={hasClickHandler ? 'button' : undefined}
      {...restProps}
    >
      {children}
    </RenderTag>
  );
};

// prop generics can't be used with forwardRef, so we have to manually cast the type
const ClickableDiv = forwardRef(ClickableDivInner) as <TElement extends GenericElement = 'div'>(
  props: Props<TElement> & React.RefAttributes<React.ElementRef<TElement>>
) => React.ReactElement;

export default ClickableDiv;
